<template>
  <section class="duallo-admin-table">
    <header v-if="!selectedScan" class="duallo-table-header">
      <div>
        <div class="duallo-eyebrow">Tenant Admin</div>
        <h3 class="duallo-table-title">Scan Pipelines</h3>
      </div>
      <div class="asset-picker">
        <v-select v-model="selectedAssetId" :items="assets" item-title="name" item-value="id" class="duallo-field"
          placeholder="Select asset" variant="outlined" density="compact" hide-details prepend-inner-icon="mdi-package-variant-closed"
          @update:model-value="onAssetSelected" />
      </div>
    </header>

    <div v-if="!selectedScan" class="duallo-table-card">
      <div v-if="isLoading" class="duallo-state-block">
        <div v-for="n in 3" :key="n" class="duallo-row-skeleton" />
      </div>

      <div v-else-if="!selectedAssetId" class="duallo-state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-package-variant-closed</v-icon>
        <p class="empty-title">Select an asset</p>
        <p class="empty-subtitle">Choose an asset above to view its scans.</p>
      </div>

      <div v-else-if="scans.length === 0" class="duallo-state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-camera-off-outline</v-icon>
        <p class="empty-title">No scans found</p>
        <p class="empty-subtitle">This asset doesn't have any scan records.</p>
      </div>

      <template v-else>
        <div v-if="!isCompact" class="duallo-table-wrap">
          <table class="duallo-data-table">
            <thead>
              <tr>
                <th class="col-name">Name</th>
                <th class="col-status">Status</th>
                <th class="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="scan in scans" :key="scan.id">
                <td class="cell-name">{{ scan.name }}</td>
                <td class="cell-status">
                  <ScanStatusPill :status="scan.status" />
                </td>
                <td class="cell-actions">
                  <button class="row-btn ghost" :disabled="!hasProvenance(scan.id)"
                    :title="hasProvenance(scan.id) ? 'Review AI detections' : 'No AI provenance for this scan yet'"
                    @click="reviewAi(scan)">
                    <v-icon size="15" start>mdi-magnify-scan</v-icon>
                    AI Review
                  </button>
                  <button class="row-btn" @click="selectScan(scan)">
                    Select
                    <v-icon size="16" end>mdi-arrow-right</v-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="duallo-card-list">
          <article v-for="scan in scans" :key="scan.id" class="duallo-list-card">
            <div class="card-head">
              <div class="card-name">{{ scan.name }}</div>
              <ScanStatusPill :status="scan.status" />
            </div>
            <div class="card-actions">
              <button class="row-btn ghost" :disabled="!hasProvenance(scan.id)"
                :title="hasProvenance(scan.id) ? 'Review AI detections' : 'No AI provenance for this scan yet'"
                @click="reviewAi(scan)">
                <v-icon size="15" start>mdi-magnify-scan</v-icon>
                AI Review
              </button>
              <button class="row-btn" @click="selectScan(scan)">
                Select
                <v-icon size="16" end>mdi-arrow-right</v-icon>
              </button>
            </div>
          </article>
        </div>
      </template>
    </div>

    <div v-else class="pipeline-view">
      <button class="back-link" @click="selectedScan = null">
        <v-icon size="16">mdi-chevron-left</v-icon>
        Back to scans
      </button>
      <ScanPipelineManager :selectedOrg="organization.organizationId" :scan="selectedScan"
        @onScanUpdated="updateScan" />
    </div>
  </section>
</template>

<script setup>
import { defineOptions, ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import AssetService from '@/services/AssetService';
import ModelVersionService from '@/services/ModelVersionService';
import MarkerProvenanceService from '@/services/MarkerProvenanceService';
import ScanStatusPill from './pills/ScanStatusPill.vue';
import ScanPipelineManager from './ScanPipelineManager.vue';
import { useOrganizationStore } from '@/store/organizationStore';

defineOptions({ name: 'ScanManagementPanel' });

const { smAndDown } = useDisplay();
const isCompact = computed(() => smAndDown.value);

const organization = useOrganizationStore();
const assets = ref([]);
const scans = ref([]);
const selectedAssetId = ref(null);
const selectedScan = ref(null);
const isLoading = ref(false);

onMounted(async () => {
  assets.value = (await AssetService.getAssetsByOrganizationIdAsync(organization.organizationId))?.filter(a => a.isActive) ?? [];
});

const onAssetSelected = async () => {
  selectedScan.value = null;
  await loadScans();
};

// scanId -> provenance record count (undefined until checked). Drives the AI Review gate.
const provenanceCounts = ref({});
const hasProvenance = (scanId) => (provenanceCounts.value[scanId] ?? 0) > 0;

const loadScans = async () => {
  if (!selectedAssetId.value) return;
  selectedScan.value = null;
  isLoading.value = true;
  provenanceCounts.value = {};
  try {
    scans.value = await ModelVersionService.getScansByAssetId(selectedAssetId.value);
  } finally {
    isLoading.value = false;
  }
  loadProvenanceCounts();
};

// Fire-and-forget: enable each scan's AI Review button once we confirm it has
// provenance. Failures leave the button disabled, which is the safe default.
const loadProvenanceCounts = async () => {
  const results = await Promise.all(
    scans.value.map(async (scan) => {
      try {
        return [scan.id, await MarkerProvenanceService.getCount(scan.id)];
      } catch {
        return [scan.id, 0];
      }
    }),
  );
  provenanceCounts.value = Object.fromEntries(results);
};

const updateScan = async () => {
  const selectedScanId = selectedScan.value.id;
  await loadScans();
  const updatedScan = scans.value.find(s => s.id === selectedScanId);
  selectScan(updatedScan);
}

const selectScan = (scan) => { selectedScan.value = scan; };

const router = useRouter();
const reviewAi = (scan) => router.push({ name: 'ProvenanceReview', params: { scanId: scan.id } });
</script>

<style scoped>
/* Shell (.duallo-admin-table / -table-header / -eyebrow / -table-title), loading/empty
   container (.duallo-state-block), skeleton, table wrap + base, and card-list come from
   the shared duallo-components.css. Kept below: this panel's asset picker, card surface,
   empty-state text, row/back buttons, pipeline view, and the per-file card override. */

.asset-picker {
  min-width: 240px;
  flex: 1 1 240px;
  max-width: 360px;
}

/* Background comes from the shared .duallo-field; keep the 10px radius override. */
.asset-picker :deep(.v-field) {
  border-radius: 10px;
}


.empty-state {
  align-items: center;
  text-align: center;
  padding: 40px 16px;
  color: #8aa0aa;
}

.empty-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #cfd8dc;
  margin: 12px 0 4px;
}

.empty-subtitle { font-size: 0.88rem; margin: 0; }

.col-name { min-width: 160px; }
.col-status { width: 160px; }
.col-actions { width: 110px; text-align: right; }

.cell-name { color: #fff; font-weight: 600; }
.cell-actions { text-align: right; white-space: nowrap; }

/* Shared list card uses gap:8 and no flex-basis; preserve this list's spacing. */
.duallo-list-card { gap: 10px; flex: 0 0 auto; }

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.card-name {
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  min-width: 0;
  flex: 1 1 auto;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
}

.row-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--duallo-accent);
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.row-btn:hover {
  background: rgba(100, 255, 218, 0.08);
}

.row-btn.ghost {
  color: #b0bec5;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-right: 6px;
}

.row-btn.ghost:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
}

.row-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.row-btn:disabled:hover {
  background: transparent;
}

/* ----- Pipeline view ----- */
.pipeline-view {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  color: #cfd8dc;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.back-link:hover {
  background: rgba(100, 255, 218, 0.08);
  border-color: rgba(100, 255, 218, 0.2);
  color: #fff;
}
</style>
